import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, CheckCircle2, CloudUpload, Info, Loader2, Plus } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { useAccounts } from '@/hooks/useFinancialData';
import { parseCSV, type ParsedTransaction } from '@/lib/csvParser';
import { formatBRL, formatDate } from '@/lib/format';
import { Sidebar } from '@/components/layout/Sidebar';
import { Header } from '@/components/layout/Header';

type Step = 'upload' | 'preview' | 'done';

interface ImportResult {
  inserted: number;
  duplicates: number;
  errors: number;
  errorDetail?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function simpleHash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h).toString(36);
}

function buildRef(t: ParsedTransaction): string {
  return t.external_ref?.trim()
    || simpleHash(`${t.date}|${t.description}|${t.amount_cents}|${t.direction}`);
}

// Busca TODOS os external_refs já importados para a conta (sem usar .in() que tem
// limite de URL). Retorna Set para lookup O(1).
async function fetchExistingRefs(accountId: string, tenantId: string): Promise<Set<string>> {
  const set = new Set<string>();
  // Busca em páginas de 1000 para cobrir contas com muitas transações
  let page = 0;
  const pageSize = 1000;
  while (true) {
    const { data, error } = await supabase
      .from('transactions')
      .select('external_ref')
      .eq('account_id', accountId)
      .eq('tenant_id', tenantId)
      .eq('source', 'csv_import')
      .not('external_ref', 'is', null)
      .range(page * pageSize, (page + 1) * pageSize - 1);

    const rows = data as unknown as Array<{ external_ref: string }>;
    if (error || !rows || rows.length === 0) break;
    rows.forEach((r) => set.add(r.external_ref));
    if (rows.length < pageSize) break;
    page++;
  }
  return set;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ImportCSV() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const { accounts } = useAccounts();

  const [step, setStep]               = useState<Step>('upload');
  const [fileName, setFileName]       = useState('');
  const [parsed, setParsed]           = useState<ParsedTransaction[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [detectedBank, setDetectedBank] = useState<string | undefined>();
  const [totalCredit, setTotalCredit] = useState(0);
  const [totalDebit, setTotalDebit]   = useState(0);

  const [accountId, setAccountId]               = useState('');
  const [newAccountName, setNewAccountName]     = useState('');
  const [newAccountInstitution, setNewAccountInstitution] = useState('');

  // Deduplicação: calculada ao selecionar conta na etapa de preview
  const [deduping, setDeduping]     = useState(false);
  const [newCount, setNewCount]     = useState<number | null>(null);
  const [dupCount, setDupCount]     = useState(0);
  const [existingRefs, setExistingRefs] = useState<Set<string>>(new Set());

  const [importing, setImporting]   = useState(false);
  const [result, setResult]         = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  // Quando a conta muda no preview, recalcula duplicatas
  useEffect(() => {
    if (step !== 'preview' || !accountId || !tenantId || parsed.length === 0) {
      setNewCount(null);
      setDupCount(0);
      setExistingRefs(new Set());
      return;
    }
    let cancelled = false;
    setDeduping(true);
    fetchExistingRefs(accountId, tenantId).then((refs) => {
      if (cancelled) return;
      const dups = parsed.filter((t) => refs.has(buildRef(t))).length;
      setExistingRefs(refs);
      setDupCount(dups);
      setNewCount(parsed.length - dups);
      setDeduping(false);
    });
    return () => { cancelled = true; };
  }, [accountId, tenantId, parsed, step]);

  const handleFile = useCallback((file: File) => {
    setFileName(file.name);
    setNewCount(null);
    setDupCount(0);
    setExistingRefs(new Set());
    setImportError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const r = parseCSV(content);
      setParsed(r.transactions);
      setParseErrors(r.errors);
      setDetectedBank(r.bank);
      setTotalCredit(r.totalCredit);
      setTotalDebit(r.totalDebit);
      setStep('preview');
    };
    reader.readAsText(file, 'UTF-8');
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  function resetToUpload() {
    setStep('upload');
    setParsed([]);
    setResult(null);
    setFileName('');
    setNewCount(null);
    setDupCount(0);
    setImportError('');
  }

  function translateImportError(msg: string): string {
    if (msg.includes('row-level security') || msg.includes('RLS') || msg.includes('42501'))
      return 'Permissão negada. Verifique se sua empresa está configurada corretamente.';
    if (msg.includes('duplicate') || msg.includes('unique') || msg.includes('23505'))
      return 'Algumas transações já existem no banco e foram ignoradas.';
    if (msg.includes('foreign key') || msg.includes('23503'))
      return 'Conta de destino não encontrada. Recarregue a página e tente novamente.';
    if (msg.includes('network') || msg.includes('fetch'))
      return 'Erro de conexão. Verifique sua internet e tente novamente.';
    return msg;
  }

  async function handleImport() {
    if (!user) return;
    if (!tenantId) {
      setImportError('Empresa não configurada. Complete o onboarding antes de importar.');
      return;
    }
    setImporting(true);
    setImportError('');

    try {
      const tid = tenantId;

      // ── Resolve conta ──────────────────────────────────────────────────────
      let resolvedAccountId = accountId;
      if (!resolvedAccountId) {
        if (!newAccountName.trim()) {
          setImportError('Selecione ou crie uma conta antes de importar.');
          setImporting(false);
          return;
        }
        const { data: acc, error: accErr } = await supabase
          .from('accounts')
          .insert({
            tenant_id: tid,
            name: newAccountName.trim(),
            type: 'checking' as const,
            institution: newAccountInstitution.trim() || null,
            balance_cents: 0,
          } as never)
          .select('id')
          .single();
        const newAcc = acc as { id: string } | null;
        if (accErr || !newAcc) throw new Error('Falha ao criar conta: ' + accErr?.message);
        resolvedAccountId = newAcc.id;
      }

      // ── Carrega regras de categorização e canal padrão em paralelo ────────
      const [{ data: rulesData }, { data: defaultChannelData }] = await Promise.all([
        supabase
          .from('category_rules')
          .select('description_pattern, category_id')
          .eq('tenant_id', tid),
        supabase
          .from('channels')
          .select('id')
          .eq('tenant_id', tid)
          .eq('is_default', true)
          .single(),
      ]);
      const rulesMap = new Map(
        (rulesData as unknown as Array<{ description_pattern: string; category_id: string }> ?? [])
          .map((r) => [r.description_pattern, r.category_id])
      );
      const defaultChannelId = (defaultChannelData as { id: string } | null)?.id ?? null;

      // ── Monta linhas ───────────────────────────────────────────────────────
      const allRows = parsed.map((t) => ({
        tenant_id: tid,
        account_id: resolvedAccountId,
        channel_id: defaultChannelId,
        direction: t.direction as 'in' | 'out',
        amount_cents: t.amount_cents,
        description: t.description,
        category_id: rulesMap.get(t.description) ?? null,
        occurred_at: new Date(t.date + 'T12:00:00').toISOString(),
        source: 'csv_import' as const,
        external_ref: buildRef(t),
        raw_payload: { raw: t.raw } as Record<string, unknown>,
        created_by: user.id,
        _parsed: t, // auxiliar, removido antes do insert
      }));

      // ── Deduplicação ───────────────────────────────────────────────────────
      // Se já fizemos o check na preview, usamos o set em memória.
      // Se a conta foi criada agora (nova), o set está vazio = tudo é novo.
      const refs = existingRefs.size > 0
        ? existingRefs
        : await fetchExistingRefs(resolvedAccountId, tid);

      const newRows = allRows.filter((r) => !refs.has(r.external_ref));
      const duplicates = allRows.length - newRows.length;

      // Remove o campo auxiliar antes de enviar ao banco
      const rowsToInsert = newRows.map(({ _parsed: _p, ...rest }) => rest);

      // ── Inserção em lotes de 200 ───────────────────────────────────────────
      let inserted  = 0;
      let errors    = 0;
      let errorDetail = '';

      for (let i = 0; i < rowsToInsert.length; i += 200) {
        const batch = rowsToInsert.slice(i, i + 200);
        const { data: insertedRows, error } = await supabase
          .from('transactions')
          .insert(batch as never)
          .select('id');

        if (error) {
          errors += batch.length;
          errorDetail = `[${error.code}] ${error.message}`;
        } else {
          inserted += (insertedRows as unknown as unknown[])?.length ?? 0;
        }
      }

      if (errors > 0 && inserted === 0 && rowsToInsert.length > 0) {
        throw new Error(`Falha ao inserir no banco: ${errorDetail}`);
      }

      // ── Atualiza saldo (só se inseriu e CSV é mais recente) ────────────────
      if (inserted > 0) {
        const lastWithBalance = [...parsed].reverse().find((t) => t.balance_cents !== undefined);
        if (lastWithBalance?.balance_cents !== undefined) {
          const { data: latestTx } = await supabase
            .from('transactions')
            .select('occurred_at')
            .eq('account_id', resolvedAccountId)
            .eq('tenant_id', tid)
            .order('occurred_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          const latestInDb = latestTx
            ? new Date((latestTx as unknown as { occurred_at: string }).occurred_at)
            : null;
          const lastCsvDate = new Date(lastWithBalance.date + 'T23:59:59');

          if (!latestInDb || lastCsvDate >= latestInDb) {
            await supabase
              .from('accounts')
              .update({ balance_cents: lastWithBalance.balance_cents } as never)
              .eq('id', resolvedAccountId);
          }
        }
      }

      setResult({ inserted, duplicates, errors, errorDetail: errors > 0 ? errorDetail : undefined });
      setStep('done');
    } catch (err: any) {
      setImportError(translateImportError(err.message ?? 'Erro desconhecido'));
    } finally {
      setImporting(false);
    }
  }

  const canImport = !importing && parsed.length > 0 && (accountId || newAccountName.trim());
  const noNewTransactions = newCount === 0 && !deduping;

  return (
    <div className="h-full flex bg-surface">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-y-auto">
          <div className="px-4 lg:px-8 py-6 max-w-4xl mx-auto">
            <button onClick={() => navigate('/financeiro')} className="btn-ghost mb-4 -ml-1">
              <ArrowLeft className="w-4 h-4" />
              Voltar ao financeiro
            </button>

            <h1 className="font-display font-bold text-2xl text-ink mb-1">Importar extrato bancário</h1>
            <p className="text-sm text-ink-muted mb-6">
              Suportamos Mercado Pago, Itaú, Bradesco, Inter, Nubank e formatos genéricos BR.
              Reimportar o mesmo arquivo é seguro — duplicatas são ignoradas automaticamente.
            </p>

            {/* ── Upload ──────────────────────────────────────────────── */}
            {step === 'upload' && (
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => inputRef.current?.click()}
                className="border-2 border-dashed border-border rounded-2xl p-16 flex flex-col items-center justify-center cursor-pointer hover:border-brand-500 hover:bg-brand-50/30 transition-colors"
              >
                <CloudUpload className="w-12 h-12 text-ink-muted mb-4" />
                <p className="font-semibold text-ink mb-1">Arraste o arquivo CSV aqui</p>
                <p className="text-sm text-ink-muted">ou clique para selecionar</p>
                <p className="text-xs text-ink-soft mt-3">CSV ou TXT · separadores ; ou ,</p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv,.txt"
                  className="sr-only"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                />
              </div>
            )}

            {/* ── Preview ─────────────────────────────────────────────── */}
            {step === 'preview' && (
              <div className="space-y-6">

                {/* Banner resumo */}
                <div className="card p-5 flex flex-wrap gap-6 items-center">
                  <div>
                    <p className="stat-label">Arquivo</p>
                    <p className="font-medium text-ink truncate max-w-[200px]">{fileName}</p>
                    {detectedBank && <p className="text-xs text-ink-muted">Banco: {detectedBank}</p>}
                  </div>
                  <div>
                    <p className="stat-label">Lançamentos no CSV</p>
                    <p className="font-display font-bold text-2xl text-ink">{parsed.length}</p>
                  </div>
                  <div>
                    <p className="stat-label">Créditos</p>
                    <p className="font-display font-bold text-2xl text-positive">{formatBRL(totalCredit)}</p>
                  </div>
                  <div>
                    <p className="stat-label">Débitos</p>
                    <p className="font-display font-bold text-2xl text-negative">{formatBRL(totalDebit)}</p>
                  </div>
                  <button onClick={resetToUpload} className="btn-ghost ml-auto">
                    Trocar arquivo
                  </button>
                </div>

                {/* Erros de parse */}
                {parseErrors.length > 0 && (
                  <div className="rounded-xl border border-warning/40 bg-warning/5 p-4 space-y-1">
                    <p className="text-sm font-semibold text-warning flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {parseErrors.length} linha(s) ignoradas no arquivo
                    </p>
                    {parseErrors.slice(0, 5).map((e, i) => (
                      <p key={i} className="text-xs text-ink-muted">{e}</p>
                    ))}
                    {parseErrors.length > 5 && (
                      <p className="text-xs text-ink-soft">… e mais {parseErrors.length - 5}</p>
                    )}
                  </div>
                )}

                {/* Conta de destino */}
                <div className="card p-5">
                  <h2 className="font-semibold text-ink mb-3">Conta de destino</h2>
                  {accounts.length > 0 && (
                    <div className="mb-4">
                      <label className="block text-xs text-ink-muted mb-1">Conta existente</label>
                      <select
                        value={accountId}
                        onChange={(e) => setAccountId(e.target.value)}
                        className="input w-full"
                      >
                        <option value="">— selecione ou crie uma nova —</option>
                        {accounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}{a.institution ? ` (${a.institution})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {!accountId && (
                    <div className="border border-dashed border-border rounded-xl p-4 space-y-3">
                      <p className="text-xs font-semibold text-ink flex items-center gap-1">
                        <Plus className="w-3.5 h-3.5" />
                        Nova conta
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-ink-muted mb-1">Nome *</label>
                          <input
                            type="text"
                            className="input w-full"
                            placeholder="ex: Mercado Pago"
                            value={newAccountName}
                            onChange={(e) => setNewAccountName(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-ink-muted mb-1">Banco / Instituição</label>
                          <input
                            type="text"
                            className="input w-full"
                            placeholder="ex: Mercado Pago"
                            value={newAccountInstitution}
                            onChange={(e) => setNewAccountInstitution(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Status da deduplicação */}
                  {accountId && (
                    <div className="mt-3">
                      {deduping ? (
                        <p className="text-xs text-ink-muted flex items-center gap-2">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Verificando duplicatas no banco…
                        </p>
                      ) : newCount !== null && (
                        <div className={`rounded-xl p-3 flex items-start gap-2 text-sm ${
                          noNewTransactions
                            ? 'bg-warning/5 border border-warning/30 text-warning'
                            : 'bg-positive/5 border border-positive/20 text-positive'
                        }`}>
                          <Info className="w-4 h-4 shrink-0 mt-0.5" />
                          <div>
                            {noNewTransactions ? (
                              <span className="font-semibold">
                                Todas as {parsed.length} transações já existem no banco para esta conta.
                                Nada novo será inserido.
                              </span>
                            ) : (
                              <span>
                                <strong>{newCount} novas</strong> serão inseridas
                                {dupCount > 0 && <> · <strong>{dupCount} já existem</strong> (ignoradas)</>}.
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Prévia das transações */}
                <div className="card p-5">
                  <h2 className="font-semibold text-ink mb-3">
                    Prévia — {Math.min(50, parsed.length)} de {parsed.length} lançamentos
                  </h2>
                  <div className="overflow-x-auto -mx-5">
                    <table className="w-full text-sm min-w-[600px]">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-ink-muted border-y border-border bg-surface-subtle/50">
                          <th className="px-5 py-2 font-medium">Data</th>
                          <th className="px-3 py-2 font-medium">Descrição</th>
                          <th className="px-3 py-2 font-medium text-right">Valor</th>
                          <th className="px-5 py-2 font-medium text-right">Saldo parcial</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsed.slice(0, 50).map((t, i) => (
                          <tr key={i} className="border-b border-border last:border-0 hover:bg-surface-subtle/40">
                            <td className="px-5 py-2 text-ink-muted text-xs whitespace-nowrap">{formatDate(t.date)}</td>
                            <td className="px-3 py-2 text-ink truncate max-w-[320px]">{t.description}</td>
                            <td className={`px-3 py-2 text-right tabular-nums font-medium ${t.direction === 'in' ? 'text-positive' : 'text-negative'}`}>
                              {t.direction === 'in' ? '+' : '−'}{formatBRL(t.amount_cents)}
                            </td>
                            <td className="px-5 py-2 text-right tabular-nums text-ink-muted text-xs">
                              {t.balance_cents !== undefined ? formatBRL(t.balance_cents) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Erro de import */}
                {importError && (
                  <div className="rounded-xl border border-negative/40 bg-negative/5 p-4 text-sm text-negative flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Erro ao importar</p>
                      <p className="mt-0.5 text-xs">{importError}</p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <button onClick={resetToUpload} className="btn-secondary">
                    Cancelar
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={!canImport || deduping}
                    className="btn-primary gap-2"
                  >
                    {importing && <Loader2 className="w-4 h-4 animate-spin" />}
                    {noNewTransactions
                      ? 'Nada novo para importar'
                      : newCount !== null
                        ? `Importar ${newCount} transações`
                        : `Importar ${parsed.length} transações`}
                  </button>
                </div>
              </div>
            )}

            {/* ── Done ────────────────────────────────────────────────── */}
            {step === 'done' && result && (
              <div className="card p-10 flex flex-col items-center text-center gap-4">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                  result.inserted > 0 ? 'bg-positive/10' : 'bg-warning/10'
                }`}>
                  <CheckCircle2 className={`w-8 h-8 ${result.inserted > 0 ? 'text-positive' : 'text-warning'}`} />
                </div>

                <h2 className="font-display font-bold text-2xl text-ink">
                  {result.inserted > 0 ? 'Importação concluída' : 'Nada novo inserido'}
                </h2>

                {result.inserted === 0 && result.duplicates > 0 && (
                  <p className="text-sm text-ink-muted max-w-sm">
                    Todas as transações deste arquivo já estavam no banco para esta conta.
                  </p>
                )}

                <div className="flex gap-8 mt-2">
                  {result.inserted > 0 && (
                    <div>
                      <p className="font-display font-bold text-3xl text-positive">{result.inserted}</p>
                      <p className="text-sm text-ink-muted">inseridas</p>
                    </div>
                  )}
                  {result.duplicates > 0 && (
                    <div>
                      <p className="font-display font-bold text-3xl text-ink-muted">{result.duplicates}</p>
                      <p className="text-sm text-ink-muted">já existiam</p>
                    </div>
                  )}
                  {result.errors > 0 && (
                    <div>
                      <p className="font-display font-bold text-3xl text-negative">{result.errors}</p>
                      <p className="text-sm text-ink-muted">com erro</p>
                    </div>
                  )}
                </div>

                {result.errorDetail && (
                  <p className="text-xs text-negative bg-negative/5 rounded-xl px-4 py-2">
                    {result.errorDetail}
                  </p>
                )}

                <div className="flex gap-3 mt-4">
                  <button onClick={resetToUpload} className="btn-secondary">
                    Importar outro arquivo
                  </button>
                  <button onClick={() => navigate('/financeiro')} className="btn-primary">
                    Ver dashboard
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
